import { z } from 'zod'

/**
 * Single source of truth for environment variables.
 *
 * Rules:
 * - `import.meta.env` must ONLY be used in this file.
 * - Every other module imports `env` from `@/shared/config`.
 * - Adding a new variable? Update:
 *     1. `.env.example` (the contract)
 *     2. The Zod schema below
 *     3. `env.d.ts` for IntelliSense
 *
 * Never put real secrets in `VITE_` variables — they are baked into the
 * client bundle and visible in DevTools. Server-only secrets (build tokens,
 * signing keys) go in `.env.local` without the `VITE_` prefix and are
 * consumed only inside `vite.config.ts`.
 */

const envSchema = z.object({
  // App metadata
  VITE_APP_TITLE: z.string().min(1).default('Market Assist'),
  VITE_APP_VERSION: z.string().default('0.0.0'),

  // API
  VITE_API_BASE_URL: z.string().url('VITE_API_BASE_URL must be a valid URL'),
  VITE_API_TIMEOUT: z.coerce.number().positive().default(30000),

  // Sentry (DSN is public — safe to expose in the bundle)
  VITE_SENTRY_DSN: z.string().url().optional(),

  // Built-in Vite env
  MODE: z.string().default('development'),
  BASE_URL: z.string().default('/'),
  DEV: z.boolean().default(false),
  PROD: z.boolean().default(false),
})

const parsed = envSchema.safeParse(import.meta.env)

if (!parsed.success) {
  if (import.meta.env.DEV) {
    console.error(
      '[env] Invalid environment configuration:',
      parsed.error.flatten().fieldErrors,
    )
  }
  throw new Error('Invalid environment configuration. See terminal output.')
}

export const env = parsed.data

export const isDev = env.DEV
export const isProd = env.PROD
